/* ────────────────────────────────────────────────────────────────────
   /library — THE FAITHARAMA BOOKSHELF
   Live-pulled from Mike's Heyzine account via /api/heyzine/flipbooks.

   Three visitor types:
     • Anon visitor       → Pick ONE book free. Email gate. Joins as free trial.
     • Free-trial member  → Full shelf unlocked, no gate.
     • Paid member        → Full shelf, no gate.
   ──────────────────────────────────────────────────────────────────── */
import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import SiteNav from "../components/SiteNav";
import SiteFooter from "../components/SiteFooter";
import { BookOpen, Lock, ExternalLink, X, Mail, Gift } from "lucide-react";

const BRAND = "#287F8E";
const GREEN = "#2E9F3A";
const ORANGE = "#F39A1F";
const WINE = "#8B2A2A";

export default function LibraryPage() {
  const { user, loading } = useAuth();
  const [flipbooks, setFlipbooks] = useState([]);
  const [busy, setBusy] = useState(true);
  const [picked, setPicked] = useState(null);

  useEffect(() => {
    api.get("/heyzine/flipbooks")
      .then((r) => setFlipbooks(r.data?.flipbooks || []))
      .catch(() => toast.error("Could not load the bookshelf. Try refreshing in a moment."))
      .finally(() => setBusy(false));
  }, []);

  // Auth gate — Library is members-only.  Free trial counts as member.
  if (loading) return null;
  if (!user) return <Navigate to="/login?next=/library" replace />;

  return (
    <div className="min-h-screen bg-white" data-testid="library-page">
      <SiteNav />

      {/* HERO */}
      <section className="border-b" style={{ background: "#EAF6FC", borderColor: `${BRAND}33` }}>
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-12 md:py-16 text-center">
          <div className="text-[11px] tracking-[0.32em] uppercase font-bold mb-3" style={{ color: BRAND }}>
            ★ THE FAITHARAMA BOOKSHELF
          </div>
          <h1 className="font-serif-display uppercase leading-tight"
              style={{ fontSize: "clamp(2rem, 4.5vw, 4rem)", color: "#0F172A", textWrap: "balance" }}>
            Pick A Book. <span style={{ color: GREEN }}>Read It Free.</span>
          </h1>
          <p className="mt-4 text-base md:text-lg max-w-2xl mx-auto" style={{ color: "#0F172A" }}>
            Every flipbook Mike has published, all in one place. Click any cover to start reading.
          </p>
        </div>
      </section>

      {/* BOOKSHELF */}
      <section className="max-w-6xl mx-auto px-4 md:px-6 py-12">
        {busy ? (
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {[1,2,3,4,5,6,7,8].map((i) => <Skeleton key={i}/>)}
          </div>
        ) : flipbooks.length === 0 ? (
          <EmptyShelf />
        ) : (
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6" data-testid="library-shelf">
            {flipbooks.map((fb) => (
              <BookCard key={fb.id} book={fb} onPick={() => setPicked(fb)} />
            ))}
          </div>
        )}
      </section>

      <SiteFooter />

      {/* READER MODAL — opens picked book in an iframe */}
      {picked && <ReaderModal book={picked} onClose={() => setPicked(null)} />}
    </div>
  );
}

/* ──────────── BookCard ──────────── */
function BookCard({ book, onPick }) {
  const tid = `book-card-${book.id.slice(0, 10)}`;
  return (
    <button type="button"
            onClick={onPick}
            data-testid={tid}
            className="group text-left rounded-xl overflow-hidden border-2 bg-white shadow-md hover:shadow-2xl hover:-translate-y-1 transition"
            style={{ borderColor: `${BRAND}33` }}>
      <div className="relative aspect-[3/4] overflow-hidden" style={{ background: "#F1F5F9" }}>
        {book.thumbnail ? (
          <img src={book.thumbnail} alt={book.title}
               loading="lazy"
               className="w-full h-full object-cover group-hover:scale-[1.04] transition"/>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <BookOpen className="w-16 h-16 opacity-30" style={{ color: BRAND }}/>
          </div>
        )}
        <div className="absolute top-2 right-2 px-2 py-1 rounded-full text-[10px] tracking-[0.2em] uppercase font-bold shadow"
             style={{ background: GREEN, color: "#FFFFFF" }}>
          ★ FREE
        </div>
      </div>
      <div className="p-4">
        <div className="font-serif-display text-base leading-tight uppercase" style={{ color: "#0F172A", fontWeight: 700 }}>
          {book.title}
        </div>
        {book.subtitle && (
          <div className="text-xs mt-1 line-clamp-2" style={{ color: "#5A6B7B" }}>{book.subtitle}</div>
        )}
        <div className="text-[11px] tracking-[0.18em] uppercase font-bold mt-2" style={{ color: BRAND }}>
          {book.page_count ? `${book.page_count} pages · ` : ""}Read →
        </div>
      </div>
    </button>
  );
}

/* ──────────── Reader Modal ──────────── */
function ReaderModal({ book, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 md:p-6"
         style={{ background: "rgba(15,23,42,0.85)" }}
         data-testid="reader-modal">
      <div className="relative w-full max-w-5xl h-full max-h-[92vh] bg-white rounded-xl overflow-hidden shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: `${BRAND}33` }}>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] tracking-[0.22em] uppercase font-bold" style={{ color: BRAND }}>★ NOW READING</div>
            <div className="font-serif-display text-base md:text-lg leading-tight truncate" style={{ color: "#0F172A" }}>
              {book.title}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {book.embed_url && (
              <a href={book.embed_url} target="_blank" rel="noopener noreferrer"
                 data-testid="reader-open-tab"
                 className="inline-flex items-center gap-1 px-3 py-2 rounded-md text-xs uppercase tracking-[0.18em] font-bold text-white"
                 style={{ background: GREEN }}>
                <ExternalLink className="w-3.5 h-3.5"/> New Tab
              </a>
            )}
            <button onClick={onClose}
                    data-testid="reader-close"
                    className="inline-flex items-center justify-center w-10 h-10 rounded-md text-white"
                    style={{ background: WINE }}>
              <X className="w-5 h-5"/>
            </button>
          </div>
        </div>
        <div className="flex-1 bg-black">
          {book.embed_url ? (
            <iframe src={book.embed_url}
                    title={book.title}
                    className="w-full h-full"
                    allow="fullscreen"
                    style={{ border: 0 }}/>
          ) : (
            <div className="flex items-center justify-center h-full text-white/70 text-sm">
              This book doesn&apos;t have a readable link yet.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ──────────── Empty + Skeleton ──────────── */
function Skeleton() {
  return (
    <div className="rounded-xl overflow-hidden border-2 bg-white" style={{ borderColor: "#E2E8F0" }}>
      <div className="aspect-[3/4] bg-gray-100 animate-pulse"/>
      <div className="p-4 space-y-2">
        <div className="h-4 bg-gray-200 animate-pulse rounded w-3/4"/>
        <div className="h-3 bg-gray-100 animate-pulse rounded w-1/2"/>
      </div>
    </div>
  );
}
function EmptyShelf() {
  return (
    <div className="rounded-2xl border-2 border-dashed p-12 text-center" style={{ borderColor: `${BRAND}55` }}>
      <BookOpen className="w-12 h-12 mx-auto mb-3" style={{ color: BRAND }}/>
      <h3 className="font-serif-display text-2xl uppercase" style={{ color: "#0F172A" }}>The Shelf Is Empty</h3>
      <p className="mt-2 text-sm text-foreground/70 max-w-md mx-auto">
        Once Mike uploads flipbooks to his Heyzine account, they&apos;ll appear here automatically.
      </p>
    </div>
  );
}
